import { useEffect, useRef, useState } from "react";

import { resolveMediaDuration } from "../../application/mediaDuration";
import { trackPlaybackUrl } from "../../application/useMeditationTracks";
import { BACKGROUND_TRACK_GAIN } from "../../domain/backgroundTracks";
import { MeditationTrack } from "../../domain/types";

export type TrackPlayback = {
    track: MeditationTrack;
    paused: boolean;
    /** Seconds in, and how long it runs once the file says. */
    position: number;
    duration: number;
};

/**
 * One track at a time, heard from a shelf: start one, pause it, move through
 * it, stop it. Starting another stops the first.
 *
 * The file is not public, so each start asks for a short-lived URL. Music
 * never outlives the screen it was started from.
 */
export function useTrackPlayer() {
    const audioRef = useRef<HTMLAudioElement | null>(null);
    // Which start is the latest: a URL that arrives after another track was
    // chosen must not start the first one over the second.
    const attempt = useRef(0);
    const [playback, setPlayback] = useState<TrackPlayback | null>(null);

    const release = () => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.pause();
        audio.ontimeupdate = null;
        audio.onended = null;
        audioRef.current = null;
    };

    useEffect(() => release, []);

    const stop = () => {
        attempt.current += 1;
        release();
        setPlayback(null);
    };

    const play = async (track: MeditationTrack) => {
        const mine = ++attempt.current;
        release();
        setPlayback({ track, paused: false, position: 0, duration: track.duration_seconds ?? 0 });
        try {
            const url = await trackPlaybackUrl(track.id);
            if (mine !== attempt.current) return;
            const audio = new Audio(url);
            audio.volume = BACKGROUND_TRACK_GAIN;
            audioRef.current = audio;
            // Music recorded in a browser may not say how long it runs.
            const length = await resolveMediaDuration(audio);
            if (mine !== attempt.current) return;
            if (length) {
                setPlayback((current) =>
                    current && current.track.id === track.id
                        ? { ...current, duration: length }
                        : current
                );
            }
            audio.ontimeupdate = () =>
                setPlayback((current) =>
                    current && current.track.id === track.id
                        ? { ...current, position: audio.currentTime }
                        : current
                );
            audio.onended = () => stop();
            await audio.play();
        } catch {
            if (mine === attempt.current) stop();
        }
    };

    /** Pause or carry on; a track not yet started is started. */
    const toggle = (track: MeditationTrack) => {
        const audio = audioRef.current;
        if (!audio || playback?.track.id !== track.id) {
            void play(track);
            return;
        }
        if (playback.paused) {
            void audio.play().catch(() => stop());
            setPlayback((current) => current && { ...current, paused: false });
        } else {
            audio.pause();
            setPlayback((current) => current && { ...current, paused: true });
        }
    };

    const seek = (seconds: number) => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.currentTime = seconds;
        setPlayback((current) => current && { ...current, position: seconds });
    };

    return { playback, toggle, seek, stop };
}
